import { Scalars } from '@/gql/generated/graphql'
import { z } from 'zod'

export const enumAttributeOptionsFilterSchema = z.record(z.string(), z.boolean())
export type EnumAttributeOptionsFilter = z.infer<typeof enumAttributeOptionsFilterSchema>

export const booleanAttributeOptionsFilterSchema = z.record(
  z.union([z.literal('true'), z.literal('false')]),
  z.boolean()
)
export type BooleanAttributeOptionsFilter = z.infer<typeof booleanAttributeOptionsFilterSchema>

export const enumAttributeFilterSchema = z.object({
  type: z.literal('enum'),
  options: enumAttributeOptionsFilterSchema
})
export type EnumAttributeFilter = z.infer<typeof enumAttributeFilterSchema>

export const booleanAttributeFilterSchema = z.object({
  type: z.literal('boolean'),
  options: booleanAttributeOptionsFilterSchema
})
export type BooleanAttributeFilter = z.infer<typeof booleanAttributeFilterSchema>

export const numberAttributeFilterSchema = z.object({
  type: z.literal('number'),
  min: z.number(),
  max: z.number(),
  domain: z.array(z.number()).length(2)
})
export type NumberAttributeFilter = z.infer<typeof numberAttributeFilterSchema>

export const attributesFilterSchema = z.record(
  z.string(),
  z.union([enumAttributeFilterSchema, numberAttributeFilterSchema, booleanAttributeFilterSchema])
)
export type AttributesFilter = z.infer<typeof attributesFilterSchema>

export const attributeFilterSchema = z.discriminatedUnion('type', [
  enumAttributeFilterSchema,
  numberAttributeFilterSchema,
  booleanAttributeFilterSchema
])
export type AttributeFilter = z.infer<typeof attributeFilterSchema>

export const queryStateEnumAttributeFilterSchema = z.object({
  type: z.literal('enum'),
  options: z.record(z.string(), z.union([z.literal('true'), z.literal('false')]))
})
export type QueryStateEnumAttributeFilter = z.infer<typeof queryStateEnumAttributeFilterSchema>

export const queryStateBooleanAttributeFilterSchema = z.object({
  type: z.literal('boolean'),
  options: z.record(z.union([z.literal('true'), z.literal('false')]), z.union([z.literal('true'), z.literal('false')]))
})
export type QueryStateBooleanAttributeFilter = z.infer<typeof queryStateBooleanAttributeFilterSchema>

export const queryStateNumberAttributeFilterSchema = z.object({
  type: z.literal('number'),
  min: z.string(),
  max: z.string(),
  domain: z.array(z.string()).length(2)
})
export type QueryStateNumberAttributeFilter = z.infer<typeof queryStateNumberAttributeFilterSchema>

export const queryStateAttributeFilterSchema = z.discriminatedUnion('type', [
  queryStateEnumAttributeFilterSchema,
  queryStateBooleanAttributeFilterSchema,
  queryStateNumberAttributeFilterSchema
])

export const queryStateAttributesFilterSchema = z.record(z.string(), queryStateAttributeFilterSchema)
export type QueryStateAttributesFilter = z.infer<typeof queryStateAttributesFilterSchema>

export type EnumAttributeField = {
  type: 'enum'
  name: string
  options: string[]
}

export type NumberAttributeField = {
  type: 'number'
  name: string
  min: number
  max: number
  step: number
}

export type BooleanAttributeField = {
  type: 'boolean'
  name: string
}

export type AttributeField = EnumAttributeField | NumberAttributeField | BooleanAttributeField

export const sortFeatures = (
  featuresOrderList: string[],
  a: [string, string | number],
  b: [string, string | number]
): number => {
  // If a does not exist in the feature fields but feature
  // b does, order b before a
  if (featuresOrderList.indexOf(a[0]) === -1 && featuresOrderList.indexOf(b[0]) > -1) {
    return 1
  }

  // If b does not exist in the feature fields but feature
  // a does, order a before b
  if (featuresOrderList.indexOf(b[0]) === -1 && featuresOrderList.indexOf(a[0]) > -1) {
    return -1
  }

  // If neither a nor b exists in feature fields leave ordering as is
  if (featuresOrderList.indexOf(a[0]) === -1 && featuresOrderList.indexOf(b[0]) === -1) {
    return 0
  }

  // If both a and b exist in feature fields sort based on the order of feature fields
  return featuresOrderList.indexOf(a[0]) - featuresOrderList.indexOf(b[0])
}

export const constructEmptyFilter = (attributeField: AttributeField): AttributeFilter => {
  switch (attributeField.type) {
    case 'enum': {
      const enumAttributeFilter: EnumAttributeFilter = {
        type: 'enum',
        options: {}
      }
      return enumAttributeFilter
    }
    case 'number': {
      const numberAttributeFilter: NumberAttributeFilter = {
        type: 'number',
        min: attributeField.min,
        max: attributeField.max,
        domain: [attributeField.min, attributeField.max]
      }
      return numberAttributeFilter
    }
    case 'boolean': {
      const booleanAttributeFilter: BooleanAttributeFilter = {
        type: 'boolean',
        options: {}
      }
      return booleanAttributeFilter
    }
  }
}

// This is a helper function to initiate the state
// of the AttributesFilter component with all filters
// turned off. It takes a list of AttributeField which
// dictate the shape of the filters.
export const constructEmptyFilters = (attributeFields?: AttributeField[]): AttributesFilter => {
  if (!attributeFields) {
    return {}
  }

  return attributeFields.reduce<AttributesFilter>((attributesFilter, attributeField) => {
    switch (attributeField.type) {
      case 'enum': {
        const attributeFilter: AttributesFilter = {}
        const enumAttributeFilter: EnumAttributeFilter = {
          type: 'enum',
          options: (attributeField.options || []).reduce<EnumAttributeOptionsFilter>((optionsFilters, option) => {
            const optionFilter: EnumAttributeOptionsFilter = {}
            optionFilter[option] = false
            return { ...optionsFilters, ...optionFilter }
          }, {})
        }
        attributeFilter[attributeField.name] = enumAttributeFilter
        return { ...attributesFilter, ...attributeFilter }
      }
      case 'number': {
        const attributeFilter: AttributesFilter = {}
        const numberAttributeFilter: NumberAttributeFilter = {
          type: 'number',
          min: attributeField.min,
          max: attributeField.max,
          domain: [attributeField.min, attributeField.max]
        }
        attributeFilter[attributeField.name] = numberAttributeFilter
        return { ...attributesFilter, ...attributeFilter }
      }
      case 'boolean': {
        const attributeFilter: AttributesFilter = {}
        const optionFilter: BooleanAttributeOptionsFilter = {}
        optionFilter['true'] = false
        optionFilter['false'] = false
        const booleanAttributeFilter: BooleanAttributeFilter = {
          type: 'boolean',
          options: optionFilter
        }
        attributeFilter[attributeField.name] = booleanAttributeFilter
        return { ...attributesFilter, ...attributeFilter }
      }
      default:
        return {}
    }
  }, {})
}

// This function is used to convert the query state
// derived from query params using the qs libary
// to a format that is compatible with the
// AttributesFilterControl. Specifically, qs
// parses everything into an object with strings
// as both keys and values so we need to convert boolean
// and number values to be actual booleans and numbers.
export const convertQueryStateFilterToAttributesFilter = (
  queryStateFilter: QueryStateAttributesFilter
): AttributesFilter => {
  const attributesFilter = Object.entries(queryStateFilter).reduce<AttributesFilter>((filter, [key, value]) => {
    switch (value.type) {
      case 'enum': {
        const formattedOptions = Object.entries(value.options).reduce<EnumAttributeOptionsFilter>(
          (options, [optionKey, optionValue]) => {
            return {
              ...options,
              [optionKey]: JSON.parse(optionValue)
            }
          },
          {}
        )

        const newValue: EnumAttributeFilter = {
          ...value,
          options: formattedOptions
        }
        return { ...filter, [key]: newValue }
      }
      case 'number': {
        const newValue: NumberAttributeFilter = {
          ...value,
          min: Number(value.min),
          max: Number(value.max),
          domain: [Number(value.domain[0]), Number(value.domain[1])]
        }
        return { ...filter, [key]: newValue }
      }
      case 'boolean': {
        const formattedOptions = Object.entries(value.options).reduce<BooleanAttributeOptionsFilter>(
          (options, [optionKey, optionValue]) => {
            return {
              ...options,
              [optionKey]: JSON.parse(optionValue)
            }
          },
          {}
        )
        const newValue: BooleanAttributeFilter = {
          ...value,
          options: formattedOptions
        }
        return { ...filter, [key]: newValue }
      }
      default: {
        return { ...filter, [key]: value }
      }
    }
  }, {})

  return attributesFilter
}

export const getActiveFilterCount = (
  attributesFilter?: AttributesFilter,
  searchInvocations?: number[],
  abListingsOnly?: boolean,
  walletAddresses?: string[]
): number => {
  if (!attributesFilter && !searchInvocations && !abListingsOnly && !walletAddresses?.length) {
    return 0
  }

  const attributesFilterCount = Object.entries(attributesFilter ?? {}).reduce((count, [, value]) => {
    if (value.type === 'enum' || value.type === 'boolean') {
      return (
        count +
        Object.entries(value.options).reduce((optionsCount, [, optionValue]) => {
          if (optionValue) {
            return optionsCount + 1
          }
          return optionsCount
        }, 0)
      )
    }

    if (value.min !== value?.domain[0] || value.max !== value.domain[1]) {
      return count + 1
    }

    return count
  }, 0)

  const searchInvocationsCount = searchInvocations?.length ?? 0
  const abListingsOnlyCount = abListingsOnly ? 1 : 0
  const walletAddressesCount = walletAddresses?.length ?? 0

  return attributesFilterCount + searchInvocationsCount + abListingsOnlyCount + walletAddressesCount
}

export const constructTokenJsonPathFilter = (filter: AttributesFilter): Scalars['jsonpath']['output'] => {
  const filterStrings = Object.entries(filter)
    .map(([featureFieldName, featureFilter]) => {
      if (featureFilter.type === 'enum' || featureFilter.type === 'boolean') {
        const enumFeatureOptionFilterStrings = Object.entries(featureFilter.options)
          .filter((feature) => feature[1])
          .map((feature) => {
            const isNumber = feature[0] && !isNaN(feature[0] as any) && !isNaN(parseFloat(feature[0]))
            const isBoolean =
              feature[0] && (feature[0].toLowerCase() === 'true' || feature[0].toLowerCase() === 'false')

            // We're accounting for artists potentially using returning somethign
            // besides a string for an enum field. Not sure if we want to do this
            // in the long run.
            let extraFilter
            if (isBoolean) {
              const boolVal = JSON.parse(feature[0].toLowerCase())
              extraFilter = `@."${featureFieldName}" == ${boolVal}`
            } else if (isNumber) {
              extraFilter = `@."${featureFieldName}" == ${parseFloat(feature[0])}`
            }

            return `@."${featureFieldName}" == "${feature[0]}" ${extraFilter ? `|| ${extraFilter}` : ''}`.trim()
          }, [])

        if (enumFeatureOptionFilterStrings.length) {
          return `(${enumFeatureOptionFilterStrings.join(' || ')})`
        } else {
          return null
        }
      } else if (featureFilter.type === 'number') {
        if (featureFilter.min !== featureFilter.domain[0] || featureFilter.max !== featureFilter.domain[1]) {
          return `(@."${featureFieldName}" >= ${featureFilter.min} && @."${featureFieldName}" <= ${featureFilter.max})`
        }

        return null
      } else {
        return null
      }
    })
    .filter((fieldFilter) => !!fieldFilter)

  if (filterStrings.length) {
    return `$ ? (${filterStrings.join(' && ')})`
  }

  return '$'
}

export const projectFeatureFieldCountsSchema = z.record(z.string(), z.record(z.string(), z.number()))
export type ProjectFeatureFieldCounts = z.infer<typeof projectFeatureFieldCountsSchema>

export const tokenFeaturesSchema = z.record(z.string(), z.union([z.string(), z.number(), z.boolean()]))
export type TokenFeatures = z.infer<typeof tokenFeaturesSchema>
